<template>
  <DossierDocumentCard
    title="Laudo de Potabilidade da Água"
    :status-options="statusOptions"
    :multiple-status="true"
    :is_applicable_answer="formData.is_applicable_answer"
    :status-requires-validity="['possui_vigente', 'possui_provisorio']"
    :status-requires-upload="['possui_vigente', 'possui_provisorio']"
    :explainer-text="explainerText"
    :mode="mode"
    :not-applicable="isNotApplicable"
    :start-collapsed="startCollapsed"
    v-model="formData"
    save-endpoint="/rest-home/dossier/water-potability"
    upload-endpoint="/rest-home/dossier/water-potability/upload"
    @saved="onSaved"
    @error="onError"
  >
    <template #fields-before>
      <!-- ── Pergunta condicional ── -->
      <div class="dossier-conditional__question">
        <label class="dossier-conditional__question-label">
          <i class="fas fa-question-circle mr-1"></i>
          O município exige laudo de potabilidade da água para ILPIs?
        </label>

        <div class="dossier-conditional__options">
          <label class="dossier-conditional__option">
            <input
              type="radio"
              value="sim"
              v-model="formData.is_applicable_answer"
              :disabled="isViewMode"
              class="dossier-conditional__radio"
            />
            <span>Sim, é exigido</span>
          </label>
          <label class="dossier-conditional__option">
            <input
              type="radio"
              value="nao"
              v-model="formData.is_applicable_answer"
              :disabled="isViewMode"
              class="dossier-conditional__radio"
            />
            <span>Não — não exigido no município</span>
          </label>
        </div>

        <div class="dossier-conditional__na-message">
          <i class="fas fa-info-circle mr-1"></i>
          O laudo de potabilidade não é exigido em todos os municípios. Como o
          município não exige, este módulo não gera pendência no Dossiê.
        </div>
      </div>

      <!-- Campos do laudo (exibidos apenas quando aplicável) -->
      <template>
        <div class="dossier-module__fields">
          <!-- Laboratório responsável -->
          <div class="dossier-module__field-group">
            <label class="dossier-module__label">Laboratório responsável</label>
            <v-text-field
              v-if="!isViewMode"
              v-model="formData.lab_name"
              dense
              outlined
              hide-details
              placeholder="Nome do laboratório acreditado"
              class="dossier-module__input"
            />
            <span v-else class="dossier-module__view-value">{{
              formData.lab_name || "—"
            }}</span>
          </div>

          <!-- Data da coleta -->
          <div
            class="dossier-module__field-group dossier-module__field-group--small"
          >
            <label class="dossier-module__label">Data da coleta</label>
            <v-text-field
              v-if="!isViewMode"
              v-model="formData.collection_date"
              type="date"
              dense
              outlined
              hide-details
              class="dossier-module__input"
            />
            <span v-else class="dossier-module__view-value">
              <i class="fas fa-calendar-alt mr-1"></i>
              {{ formatDateBR(formData.collection_date) }}
            </span>
          </div>

          <!-- Resultado -->
          <div
            class="dossier-module__field-group dossier-module__field-group--small"
          >
            <label class="dossier-module__label">Resultado</label>
            <v-select
              v-if="!isViewMode"
              v-model="formData.result"
              :items="results"
              dense
              outlined
              hide-details
              placeholder="Selecione"
              class="dossier-module__input"
            />
            <span
              v-else
              class="dossier-module__view-value"
              :class="resultClass"
            >
              {{ formData.result || "—" }}
            </span>
          </div>
        </div>

        <!-- Alerta: resultado impróprio -->
        <div
          v-if="formData.result === 'Impróprio para consumo'"
          class="dossier-potability__alert"
        >
          <i class="fas fa-exclamation-triangle mr-1"></i>
          Resultado impróprio para consumo. Registre as ações corretivas tomadas
          no campo de observações abaixo.
        </div>
      </template>
    </template>
  </DossierDocumentCard>
</template>

<script>
import DossierDocumentCard from "../dossier/DossierDocumentCard.vue";

const EXPLAINER_TEXT = `
  O Laudo de Potabilidade atesta que a água distribuída na ILPI está própria
  para consumo humano, conforme os parâmetros da Portaria MS nº 888/2021.<br><br>
  A exigência do laudo varia por município. Em alguns casos, o fornecimento
  por concessionária pública isenta a instituição da necessidade de laudo
  próprio. Consulte a Vigilância Sanitária local.<br><br>
  Quando exigido, deve ser realizado por laboratório acreditado e
  renovado conforme periodicidade definida pelo órgão competente.<br><br>
  O Sistema Clin não valida o conteúdo do documento, apenas registra sua
  existência para fins de organização e apresentação à fiscalização.
`;

export default {
  name: "DossierWaterPotability",
  components: { DossierDocumentCard },
  props: {
    mode: {
      type: String,
      default: "edit",
      validator: (v) => ["edit", "view"].includes(v),
    },
    initialData: { type: Object, default: () => ({}) },
    startCollapsed: { type: Boolean, default: false },
  },
  data() {
    return {
      explainerText: EXPLAINER_TEXT,
      statusOptions: [
        { value: "possui_vigente", label: "Possui laudo vigente" },
        { value: "possui_provisorio", label: "Possui laudo provisório" },
        { value: "em_elaboracao", label: "Coleta agendada / laudo em análise" },
        { value: "nao_possui", label: "Não possui (pendente)" },
      ],
      results: [
        "Próprio para consumo",
        "Próprio com ressalvas",
        "Impróprio para consumo",
        "Aguardando resultado",
      ],
      formData: {
        is_applicable_answer: null,
        lab_name: "",
        collection_date: null,
        result: null,
        status: [],
        validity_date: null,
        file_url: null,
        file_name: null,
        observations: "",
        ...this.flattenInitialData(),
      },
    };
  },
  computed: {
    isViewMode() {
      return this.mode === "view";
    },
    isNotApplicable() {
      return false;
    },
    resultClass() {
      const map = {
        "Próprio para consumo": "dossier-module__result--ok",
        "Próprio com ressalvas": "dossier-module__result--warning",
        "Impróprio para consumo": "dossier-module__result--error",
      };
      return map[this.formData.result] || "";
    },
  },
  watch: {
    initialData: {
      deep: true,
      handler() {
        Object.assign(this.formData, this.flattenInitialData());
      },
    },
  },
  methods: {
    flattenInitialData() {
      const d = this.initialData || {};
      return {
        is_applicable_answer: d.is_applicable_answer || null,
        lab_name: d.lab_name || "",
        collection_date: d.collection_date || null,
        result: d.result || null,
        status: d.status || [],
        validity_date: d.validity_date || null,
        file_url: d.file_url || null,
        file_name: d.file_name || null,
        observations: d.observations || "",
      };
    },
    onSaved(data) {
      this.$emit("saved", { module: "water_potability", data });
    },
    onError(errors) {
      this.$emit("error", { module: "water_potability", errors });
    },
    formatDateBR(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    },
  },
};
</script>

<style scoped>
.dossier-potability__alert {
  font-size: 13px;
  font-weight: 600;
  color: #842029;
  background: #f8d7da;
  border: 1px solid #dc3545;
  border-radius: 4px;
  padding: 10px 12px;
  margin-bottom: 12px;
}
</style>